import { logger } from '@/core/logger';
import { asyncStorage } from '@/core/storage';
import { useEffect, useState } from 'react';

const PROPERTIES_LAYOUT_KEY = 'properties.layout';

export type PropertiesLayout = 'grid' | 'list';

/**
 * Hook to manage and persist properties screen layout preference
 * Stores the preference in AsyncStorage and loads it on mount
 */
export function usePropertiesLayout() {
    const [layout, setLayout] = useState<PropertiesLayout>('grid');
    const [isLoading, setIsLoading] = useState(true);

    // Load layout preference on mount
    useEffect(() => {
        const loadLayout = async () => {
            try {
                const savedLayout = await asyncStorage.getItem(PROPERTIES_LAYOUT_KEY);
                if (savedLayout && (savedLayout === 'grid' || savedLayout === 'list')) {
                    setLayout(savedLayout as PropertiesLayout);
                    logger.debug('PropertiesLayout:Loaded saved layout', { layout: savedLayout });
                } else {
                    logger.debug('PropertiesLayout:No saved layout found, using default', { default: 'grid' });
                }
            } catch (error) {
                logger.error('PropertiesLayout:Failed to load layout preference', error);
            } finally {
                setIsLoading(false);
            }
        };

        loadLayout();
    }, []);

    // Save layout preference whenever it changes
    const updateLayout = async (newLayout: PropertiesLayout) => {
        setLayout(newLayout);
        try {
            await asyncStorage.setItem(PROPERTIES_LAYOUT_KEY, newLayout);
            logger.debug('PropertiesLayout:Layout preference saved', { layout: newLayout });
        } catch (error) {
            logger.error('PropertiesLayout:Failed to save layout preference', error);
        }
    };

    return {
        layout,
        setLayout: updateLayout,
        isLoading,
    };
}

